import type { Scan, ScanStage, ScanStreamEvent } from "@hobbyshopify/backend-client";

/**
 * Turning the scan pipeline's event stream into something a screen can draw.
 *
 * The stream itself is `client.scans.startStream` — transport, framing and the
 * typed event union all live in `backend-client`. What is here is the part that
 * is only true of this app: four labelled stages, in order, each with a state.
 *
 * `applyScanEvent` is pure, so the progress bar is testable without a backend.
 */

export type StageStatus = "pending" | "active" | "complete" | "error";

export const SCAN_STAGES: { key: ScanStage; label: string }[] = [
  { key: "preparation", label: "Preparing" },
  { key: "identification", label: "Identifying" },
  { key: "pricing", label: "Pricing" },
  { key: "persistence", label: "Saving" },
];

export type ScanProgress = {
  scanId?: string;
  status: "idle" | "scanning" | "completed" | "error";
  stages: Record<ScanStage, StageStatus>;
  result: Scan | null;
  error: string | null;
};

const PENDING: Record<ScanStage, StageStatus> = {
  preparation: "pending",
  identification: "pending",
  pricing: "pending",
  persistence: "pending",
};

export const initialScanProgress = (): ScanProgress => ({
  status: "idle",
  stages: { ...PENDING },
  result: null,
  error: null,
});

/** Marks one stage complete and moves the cursor to the next. */
function advance(
  stages: Record<ScanStage, StageStatus>,
  done: ScanStage,
  next?: ScanStage,
): Record<ScanStage, StageStatus> {
  return { ...stages, [done]: "complete", ...(next ? { [next]: "active" as const } : {}) };
}

/** Folds one stream event into the progress state. Pure, so it is easy to test. */
export function applyScanEvent(state: ScanProgress, event: ScanStreamEvent): ScanProgress {
  switch (event.type) {
    case "scan.started":
      return {
        ...state,
        scanId: event.data.scanId ?? state.scanId,
        status: "scanning",
        stages: { ...PENDING, preparation: "active" },
      };
    case "scan.prepared":
      return { ...state, stages: advance(state.stages, "preparation", "identification") };
    case "scan.identified":
      return { ...state, stages: advance(state.stages, "identification", "pricing") };
    case "scan.priced":
      return { ...state, stages: advance(state.stages, "pricing", "persistence") };
    case "scan.completed":
      return {
        status: "completed",
        stages: {
          preparation: "complete",
          identification: "complete",
          pricing: event.data.recognitionOutcome === "no_match" ? "pending" : "complete",
          persistence: "complete",
        },
        result: event.data,
        error: null,
      };
    case "scan.error":
      return {
        ...state,
        status: "error",
        stages: { ...state.stages, [event.data.stage]: "error" },
        error: event.data.error || "The scan could not be completed.",
      };
    default:
      return state;
  }
}
